import React from 'react';
import { Drawer, Button, Typography, Select } from 'antd';
import {
  BarChartOutlined,
  CheckOutlined,
  LineChartOutlined,
  AreaChartOutlined,
  DotChartOutlined,
  StockOutlined,
} from '@ant-design/icons';
import './IndicatorPanel.css';
import {
  INDICATOR_DEFAULT_PLACEMENT,
  INDICATOR_METADATA,
  INDICATOR_LIST,
  IndicatorId,
  IndicatorPlacement,
} from '../../constants/indicators';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

interface IndicatorPanelProps {
  open: boolean;
  onClose: () => void;
  selectedIndicators: IndicatorId[];
  onToggleIndicator: (indicatorId: IndicatorId) => void;
  indicatorPlacements: Record<IndicatorId, IndicatorPlacement>;
  onPlacementChange: (indicatorId: IndicatorId, placement: IndicatorPlacement) => void;
  theme: {
    surfaceBackground: string;
    textPrimary: string;
    textSecondary: string;
    surfaceBorder: string;
  };
}

const IndicatorPanel: React.FC<IndicatorPanelProps> = ({
  open,
  onClose,
  selectedIndicators,
  onToggleIndicator,
  indicatorPlacements,
  onPlacementChange,
  theme,
}) => {
  const { t } = useTranslation();
  const indicatorIconMap: Record<IndicatorId, React.ReactNode> = {
    volume: <BarChartOutlined />,
    rsi: <LineChartOutlined />,
  macd: <AreaChartOutlined />,
  sma: <LineChartOutlined />,
  ema: <LineChartOutlined />,
  wma: <LineChartOutlined />,
  wema: <LineChartOutlined />,
  typicalPrice: <DotChartOutlined />,
  obv: <LineChartOutlined />,
  bollingerBands: <LineChartOutlined />,
  williamsR: <AreaChartOutlined />,
  atr: <AreaChartOutlined />,
  mfi: <BarChartOutlined />,
  stochastic: <AreaChartOutlined />,
  roc: <LineChartOutlined />,
  trix: <LineChartOutlined />,
  vwap: <LineChartOutlined />,
  adl: <AreaChartOutlined />,
  adx: <AreaChartOutlined />,
  ao: <AreaChartOutlined />,
  cci: <AreaChartOutlined />,
  forceIndex: <LineChartOutlined />,
  kst: <LineChartOutlined />,
  psar: <DotChartOutlined />,
  stochRsi: <AreaChartOutlined />,
  volumeProfile: <BarChartOutlined />,
  ichimokuCloud: <LineChartOutlined />,
};

  const overlayCapable: Set<IndicatorId> = React.useMemo(
    () =>
      new Set<IndicatorId>([
        'rsi',
        'sma',
        'ema',
        'wma',
        'wema',
        'typicalPrice',
        'bollingerBands',
        'vwap',
        'psar',
        'ichimokuCloud',
        'kst',
      ]),
    []
  );

  const sortedIndicatorIds = React.useMemo(
    () =>
      [...INDICATOR_LIST].sort((a, b) =>
        t(INDICATOR_METADATA[a].labelKey).localeCompare(t(INDICATOR_METADATA[b].labelKey))
      ),
    [t]
  );

  return (
    <Drawer
      title={t('indicator.panel.title')}
      placement="right"
      width={520}
      onClose={onClose}
      open={open}
      bodyStyle={{
        padding: 0,
        background: theme.surfaceBackground,
      }}
      headerStyle={{
        borderBottom: `1px solid ${theme.surfaceBorder}`,
      }}
    >
      <div
        className="indicator-panel"
        style={{
          background: theme.surfaceBackground,
          color: theme.textPrimary,
        }}
      >
        <div className="indicator-panel__content">
          <div className="indicator-panel__header">
            <Title level={4} style={{ margin: 0 }}>
              {t('indicator.panel.title')}
            </Title>
            <Text type="secondary" style={{ color: theme.textSecondary }}>
              {t('indicator.panel.subtitle')}
            </Text>
          </div>

          <div className="indicator-panel__list">
            {sortedIndicatorIds.map((indicatorId) => {
              const meta = INDICATOR_METADATA[indicatorId];
              const isSelected = selectedIndicators.includes(indicatorId);
              return (
                <div
                  key={indicatorId}
                  className="indicator-panel__card"
                  style={{
                    borderColor: theme.surfaceBorder,
                    background: theme.surfaceBackground,
                  }}
                >
                  <div>
                    <div className="indicator-panel__card-title">
                      {indicatorIconMap[indicatorId]}
                      <Text strong>{t(meta.labelKey)}</Text>
                    </div>
                    <Text type="secondary" style={{ color: theme.textSecondary }}>
                      {t(meta.descriptionKey)}
                    </Text>
                    {overlayCapable.has(indicatorId) && (
                      <div style={{ marginTop: 12, maxWidth: 220 }}>
                        <Text style={{ display: 'block', marginBottom: 4, color: theme.textSecondary }} type="secondary">
                          {t('indicator.placement.label')}
                        </Text>
                        <Select
                          size="small"
                          value={indicatorPlacements[indicatorId] || INDICATOR_DEFAULT_PLACEMENT[indicatorId]}
                          onChange={(value) =>
                            onPlacementChange(indicatorId, value as IndicatorPlacement)
                          }
                          style={{ width: '100%' }}
                          options={[
                            { label: t('indicator.placement.main'), value: 'main' },
                            { label: t('indicator.placement.pane'), value: 'pane' },
                          ]}
                        />
                      </div>
                    )}
                  </div>
                  <Button
                    type={isSelected ? 'primary' : 'default'}
                    icon={isSelected ? <CheckOutlined /> : undefined}
                    onClick={() => onToggleIndicator(indicatorId)}
                  >
                    {isSelected ? t('indicator.added') : t('indicator.add')}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default IndicatorPanel;
